import { type NextRouter } from 'next/router'
import { compile, match, parse } from 'path-to-regexp'

import definitions, { RouteDefinition } from './definitions'

export const routes = definitions

export type TransitionOptions = {
  shallow?: boolean
  locale?: string | false
  scroll?: boolean
  unstable_skipClientCache?: boolean
}

const defaultParams = { lang: 'fr' }

// Retrieve the Next.js router instance on the client side
function getRouter(): NextRouter {
  if (typeof window === 'undefined') {
    throw new Error(
      '[Router] getRouter() appelé en contexte SSR. Utiliser uniquement côté client.'
    )
  }
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  return require('next/router').default
}

// Memoize compile() calls to avoid recompiling the same pattern on every call
const _compileCache = new Map<string, ReturnType<typeof compile>>()

// Compile a route pattern with parameters
function buildUrl(pattern: string, params: Record<string, any> = {}): string {
  try {
    // Split params containing '/' into arrays (next-routes behaviour)
    const processedParams: Record<string, any> = {}
    for (const key of Object.keys(params)) {
      const val = params[key]
      if (typeof val === 'string' && val.includes('/')) {
        processedParams[key] = val.split('/')
      } else {
        processedParams[key] = val
      }
    }
    let compileFn = _compileCache.get(pattern)
    if (!compileFn) {
      compileFn = compile(pattern)
      _compileCache.set(pattern, compileFn)
    }
    // encode option is passed to the returned function in path-to-regexp v3
    const pathUrl = compileFn(processedParams, { encode: encodeURIComponent })

    // Append params not consumed by the path pattern as query string
    const pathParamKeys = new Set(
      parse(pattern)
        .filter((t) => typeof t === 'object')
        .map((t) => String((t as { name: string | number }).name))
    )
    const queryParams: Record<string, string> = {}
    for (const key of Object.keys(params)) {
      if (
        !pathParamKeys.has(key) &&
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ''
      ) {
        queryParams[key] = Array.isArray(params[key])
          ? params[key].join(',')
          : String(params[key])
      }
    }
    const qs = new URLSearchParams(queryParams).toString().replace(/%2C/g, ',')
    return qs ? `${pathUrl}?${qs}` : pathUrl
  } catch (e) {
    console.error('buildUrl error', e)
    return ''
  }
}

// Resolve the best i18n pattern: _as > lang-specific > base
function resolvePattern(routeDef: RouteDefinition, lang?: string): string {
  if (routeDef.as) {
    return routeDef.as
  }
  if (lang && routeDef.i18n_paths) {
    for (const i18nMap of routeDef.i18n_paths) {
      if (i18nMap[lang]) {
        return i18nMap[lang]
      }
    }
  }
  return routeDef.path
}

class Router {
  static match = (
    url: string
  ): { route?: RouteDefinition; query?: Record<string, any> } | null => {
    for (const routeDef of Object.values(definitions) as RouteDefinition[]) {
      const matchFn = match(routeDef.path)
      const result = matchFn(url)
      if (result) {
        return { route: routeDef, query: result.params as Record<string, any> }
      }
      // Check i18n paths
      if (routeDef.i18n_paths) {
        for (const i18nMap of routeDef.i18n_paths) {
          for (const pattern of Object.values(i18nMap)) {
            const matchI18n = match(pattern as string)
            const r = matchI18n(url)
            if (r) {
              return {
                route: routeDef,
                query: r.params as Record<string, any>,
              }
            }
          }
        }
      }
      if (routeDef.as) {
        const matchAs = match(routeDef.as)
        const r = matchAs(url)
        if (r) {
          return { route: routeDef, query: r.params as Record<string, any> }
        }
      }
    }
    return null
  }

  static push = (
    url: string,
    as?: string,
    options?: TransitionOptions
  ): Promise<boolean> => {
    return getRouter().push(url, as, options)
  }

  static replace = (
    url: string,
    as?: string,
    options?: TransitionOptions
  ): Promise<boolean> => {
    return getRouter().replace(url, as, options)
  }

  static prefetch = (url: string): Promise<void> => {
    return getRouter().prefetch(url)
  }

  static pushRoute = (
    route: string | RouteDefinition,
    params: Record<string, any> = {},
    options: TransitionOptions = {}
  ): Promise<boolean> => {
    const { href, nextAs } = Router.getRouteConfig(route, params)
    if (!href)
      return Promise.reject(new Error(`Route not found: ${String(route)}`))
    return getRouter().push(href, nextAs, options)
  }

  static replaceRoute = (
    route: string | RouteDefinition,
    params: Record<string, any> = {}
  ): Promise<boolean> => {
    const { href, nextAs } = Router.getRouteConfig(route, params)
    if (!href)
      return Promise.reject(new Error(`Route not found: ${String(route)}`))
    return getRouter().replace(href, nextAs)
  }

  static prefetchRoute = (
    route: string | RouteDefinition,
    params: Record<string, any> = {}
  ): Promise<void> => {
    const { href } = Router.getRouteConfig(route, params)
    if (!href)
      return Promise.reject(new Error(`Route not found: ${String(route)}`))
    return getRouter().prefetch(href)
  }

  static getRouteDefinition = (
    route: string | RouteDefinition
  ): RouteDefinition | undefined => {
    if (typeof route === 'string') return (routes as any)?.[route]
    return route
  }

  static getRouteConfig = (
    route: string | RouteDefinition,
    params: Record<string, any> = {}
  ): { href?: string; nextAs?: string } => {
    const routeDef = Router.getRouteDefinition(route)

    if (!routeDef) {
      console.error('Router : route not found', route)
      return {}
    }

    const { lang, ...rParams } = params
    const pattern = resolvePattern(routeDef, lang)
    const url = buildUrl(pattern, rParams)

    // Extract query string from the built URL and append it to href so
    // router.query is populated correctly in Next.js Pages Router.
    const qsIndex = url.indexOf('?')
    const qs = qsIndex !== -1 ? url.slice(qsIndex) : ''
    const baseHref = `/${routeDef.component}`.replace('//', '/')

    return {
      href: qs ? `${baseHref}${qs}` : baseHref,
      nextAs: url || undefined,
    }
  }

  static getRouteUrl = (
    route: string | RouteDefinition,
    params: Record<string, any> = {}
  ): string => {
    const routeDef = Router.getRouteDefinition(route)

    if (!routeDef) {
      return ''
    }

    const { lang, ...rParams } = params

    // Inject default lang param when the pattern requires it
    const p: Record<string, any> = {
      ...(routeDef.path?.includes(':lang') ? defaultParams : {}),
      ...rParams,
    }

    const pattern = resolvePattern(routeDef, lang)

    let routeUrl = buildUrl(pattern, p)

    if (!routeUrl) return ''

    // Fallback: replace remaining param placeholders
    Object.keys(p).forEach((key) => {
      routeUrl = routeUrl.replace(new RegExp(`:${key}\\??`, 'gm'), p[key])
    })
    routeUrl = decodeURIComponent(routeUrl)
    routeUrl = routeUrl.replace('//', '/')

    return routeUrl
  }

  static transformHrefToRouteConfig = (
    href?: string,
    target?: string
  ): { href?: string; target?: string; nextAs?: string } => {
    try {
      if (href) {
        const matchResult = Router.match(href)
        if (matchResult?.route) {
          return {
            ...Router.getRouteConfig(matchResult.route, matchResult.query),
            ...(target && { target }),
          }
        }
      }
    } catch (e) {
      // fallthrough
    }
    return { href, ...(target && { target }) }
  }
}

export default Router
